import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// SuperAdmin — Paiements de la plateforme.
//  • Active / désactive les moyens de paiement.
//  • Choisit la portée : abonnements Zifek uniquement, ou tous les
//    paiements (commerces et clients finaux sur leurs boutiques).
//  • Fixe la commission de la plateforme (6 % par défaut) et
//    détaille ce que ces frais recouvrent (FeexPay + Mobile Money).
//  • Tableau de bord des transactions (abonnements + paiements commerces).
// ─────────────────────────────────────────────────────────────

interface ProviderRow {
  id: number;
  provider: string;
  label: string;
  description: string | null;
  enabled: boolean;
  mode: string;
  secret_configured: boolean;
  sort_order: number;
}

interface FeeLine {
  label: string;
  percent: number;
}

interface PlatformSettings {
  scope: 'subscriptions' | 'all';
  commission_percent: number;
  fee_breakdown: FeeLine[];
}

interface SubscriptionRow {
  id: number;
  user_id: number;
  plan: string | null;
  period: string | null;
  prix: number | null;
  monaie: string | null;
  status: string | null;
  payment_method: string | null;
  last_payment_id: string | null;
  created_at: string | null;
}

interface MerchantPaymentRow {
  id: number;
  user_id: number | null;
  amount: number | null;
  currency: string | null;
  method: string | null;
  status: string | null;
  reference: string | null;
  created_at: string | null;
  payload: string | null;
}

/** Numéro de commande boutique stocké dans le payload de la transaction. */
function orderRefOf(row: MerchantPaymentRow): string {
  if (!row.payload) return '—';
  try {
    const parsed = JSON.parse(row.payload) as { order_id?: number | string };
    const id = Number(parsed?.order_id);
    return id > 0 ? `#${id}` : '—';
  } catch {
    return '—';
  }
}

const DEFAULT_BREAKDOWN: FeeLine[] = [
  { label: 'Frais FeexPay (passerelle / agrégateur)', percent: 2 },
  { label: 'Frais opérateur Mobile Money', percent: 1.5 },
  { label: 'Commission Zifek', percent: 2.5 },
];

const STATUS_STYLE: Record<string, string> = {
  paid: 'bg-accent-100 text-accent-700',
  active: 'bg-accent-100 text-accent-700',
  success: 'bg-accent-100 text-accent-700',
  pending: 'bg-secondary-100 text-secondary-700',
  failed: 'bg-red-50 text-red-600',
  cancelled: 'bg-background-100 text-foreground-500',
};

function statusLabel(status: string | null): string {
  const key = (status || '').toLowerCase();
  const map: Record<string, string> = {
    paid: 'Payé',
    active: 'Actif',
    success: 'Réussi',
    pending: 'En attente',
    failed: 'Échoué',
    cancelled: 'Annulé',
  };
  return map[key] || status || '—';
}

function methodLabel(method: string | null): string {
  if (!method) return '—';
  const map: Record<string, string> = {
    feexpay: 'FeexPay',
    stripe: 'Stripe',
    paypal: 'PayPal',
    cmi: 'CMI',
    wave: 'Wave',
    flutterwave: 'Flutterwave',
    mobile_money: 'Mobile Money',
    card: 'Carte bancaire',
    cash: 'Espèces',
  };
  return map[method.toLowerCase()] || method;
}

function formatDate(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}

function money(value: number, currency = 'MAD'): string {
  return `${(Number(value) || 0).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} ${currency}`;
}

export default function SuperAdminPaymentsPage() {
  const [providers, setProviders] = useState<ProviderRow[]>([]);
  const [settings, setSettings] = useState<PlatformSettings>({
    scope: 'subscriptions',
    commission_percent: 6,
    fee_breakdown: DEFAULT_BREAKDOWN,
  });
  const [subscriptions, setSubscriptions] = useState<SubscriptionRow[]>([]);
  const [merchantPayments, setMerchantPayments] = useState<MerchantPaymentRow[]>([]);
  const [userNames, setUserNames] = useState<Record<number, string>>({});
  const [tab, setTab] = useState<'subscriptions' | 'merchants'>('subscriptions');
  const [loading, setLoading] = useState(true);
  const [savingProviders, setSavingProviders] = useState<number | null>(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [providersRes, settingsRes, subsRes, paymentsRes] = await Promise.all([
        supabase.from('payment_providers').select('id, provider, label, description, enabled, mode, secret_configured, sort_order').order('sort_order'),
        supabase.from('platform_payment_settings').select('scope, commission_percent, fee_breakdown').eq('id', 1).maybeSingle(),
        supabase
          .from('package_subscriptions')
          .select('id, user_id, plan, period, prix, monaie, status, payment_method, last_payment_id, created_at')
          .order('created_at', { ascending: false })
          .limit(300),
        supabase
          .from('zifek_payments')
          .select('id, user_id, amount, currency, method, status, reference, created_at, payload')
          .order('created_at', { ascending: false })
          .limit(300),
      ]);

      setProviders((providersRes.data || []) as ProviderRow[]);
      setSubscriptions((subsRes.data || []) as SubscriptionRow[]);
      setMerchantPayments((paymentsRes.data || []) as MerchantPaymentRow[]);

      const s = settingsRes.data as { scope?: string; commission_percent?: number; fee_breakdown?: unknown } | null;
      if (s) {
        const breakdown = Array.isArray(s.fee_breakdown) && s.fee_breakdown.length > 0
          ? (s.fee_breakdown as FeeLine[]).filter((l) => l && typeof l.label === 'string')
          : DEFAULT_BREAKDOWN;
        setSettings({
          scope: s.scope === 'all' ? 'all' : 'subscriptions',
          commission_percent: Number(s.commission_percent) || 6,
          fee_breakdown: breakdown,
        });
      }

      const ids = Array.from(new Set([
        ...(subsRes.data || []).map((r) => Number(r.user_id)),
        ...(paymentsRes.data || []).map((r) => Number(r.user_id)),
      ].filter((id) => Number.isFinite(id) && id > 0)));

      if (ids.length > 0) {
        const { data: usersData } = await supabase.from('users').select('id, name, user_name').in('id', ids);
        const map: Record<number, string> = {};
        (usersData || []).forEach((u) => {
          map[Number(u.id)] = u.name || u.user_name || `Utilisateur #${u.id}`;
        });
        setUserNames(map);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const toggleProvider = async (provider: ProviderRow) => {
    setSavingProviders(provider.id);
    setError('');
    try {
      const next = !provider.enabled;
      const { error: err } = await supabase
        .from('payment_providers')
        .update({ enabled: next, updated_at: new Date().toISOString() })
        .eq('id', provider.id);
      if (err) throw err;
      setProviders((prev) => prev.map((p) => (p.id === provider.id ? { ...p, enabled: next } : p)));
      setNotice(`${provider.label} ${next ? 'activé' : 'désactivé'} pour la plateforme.`);
      window.setTimeout(() => setNotice(''), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible de modifier ce moyen de paiement.');
    } finally {
      setSavingProviders(null);
    }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    setError('');
    try {
      const { error: err } = await supabase
        .from('platform_payment_settings')
        .upsert({
          id: 1,
          scope: settings.scope,
          commission_percent: Number(settings.commission_percent) || 0,
          fee_breakdown: settings.fee_breakdown,
          updated_at: new Date().toISOString(),
        }, { onConflict: 'id' });
      if (err) throw err;
      setNotice('Paramètres de paiement enregistrés.');
      window.setTimeout(() => setNotice(''), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Impossible d'enregistrer les paramètres.");
    } finally {
      setSavingSettings(false);
    }
  };

  const breakdownTotal = useMemo(
    () => Math.round(settings.fee_breakdown.reduce((s, l) => s + (Number(l.percent) || 0), 0) * 100) / 100,
    [settings.fee_breakdown],
  );

  const paidSubscriptions = subscriptions.filter((s) => (s.status || '').toLowerCase() === 'paid');
  const paidMerchants = merchantPayments.filter((p) => ['paid', 'success'].includes((p.status || '').toLowerCase()));

  const kpis = useMemo(() => {
    const subTotal = paidSubscriptions.reduce((s, r) => s + (Number(r.prix) || 0), 0);
    const merchantTotal = paidMerchants.reduce((s, r) => s + (Number(r.amount) || 0), 0);
    const total = subTotal + merchantTotal;
    const commission = Math.round(total * (Number(settings.commission_percent) || 0)) / 100;
    return {
      subTotal,
      merchantTotal,
      total,
      commission,
      count: paidSubscriptions.length + paidMerchants.length,
    };
  }, [paidSubscriptions, paidMerchants, settings.commission_percent]);

  const enabledProviders = providers.filter((p) => p.enabled);

  return (
    <div className="p-4 md:p-6">
      <div className="mb-6 flex flex-col md:flex-row md:items-start md:justify-between gap-3">
        <div>
          <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">
            <i className="ri-secure-payment-line mr-2 text-primary-500"></i>Paiements de la plateforme
          </h2>
          <p className="text-sm text-foreground-500 mt-1">
            Moyens de paiement, portée, commission Zifek et suivi des transactions.
          </p>
        </div>
        <Link
          to="/superadmin/logs?tab=payments"
          className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border border-background-200/70 bg-background-50 text-xs font-medium text-foreground-700 no-underline hover:bg-background-100 transition-colors whitespace-nowrap self-start"
        >
          <i className="ri-error-warning-line text-red-500"></i>
          Journal des paiements échoués
        </Link>
      </div>

      {error && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-red-50 text-red-600 text-sm flex items-center gap-2">
          <i className="ri-error-warning-line"></i>{error}
          <button onClick={() => setError('')} className="ml-auto cursor-pointer"><i className="ri-close-line"></i></button>
        </div>
      )}
      {notice && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-secondary-50 text-secondary-800 text-sm flex items-center gap-2">
          <i className="ri-checkbox-circle-line"></i>{notice}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
        </div>
      ) : (
        <div className="space-y-6">
          {/* KPI */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
              <p className="text-[11px] font-medium text-foreground-500 uppercase tracking-wide">Encaissé total</p>
              <p className="text-lg font-bold font-heading text-foreground-950">{money(kpis.total)}</p>
              <p className="text-[11px] text-foreground-400 mt-0.5">{kpis.count} transactions payées</p>
            </div>
            <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
              <p className="text-[11px] font-medium text-foreground-500 uppercase tracking-wide">Abonnements</p>
              <p className="text-lg font-bold font-heading text-foreground-950">{money(kpis.subTotal)}</p>
              <p className="text-[11px] text-foreground-400 mt-0.5">{paidSubscriptions.length} paiements</p>
            </div>
            <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
              <p className="text-[11px] font-medium text-foreground-500 uppercase tracking-wide">Paiements commerces</p>
              <p className="text-lg font-bold font-heading text-foreground-950">{money(kpis.merchantTotal)}</p>
              <p className="text-[11px] text-foreground-400 mt-0.5">{paidMerchants.length} paiements</p>
            </div>
            <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
              <p className="text-[11px] font-medium text-foreground-500 uppercase tracking-wide">Commission Zifek</p>
              <p className="text-lg font-bold font-heading text-primary-700">{money(kpis.commission)}</p>
              <p className="text-[11px] text-foreground-400 mt-0.5">{settings.commission_percent} % des transactions</p>
            </div>
          </div>

          {/* Moyens de paiement */}
          <section className="bg-background-50 border border-background-200/70 rounded-lg p-5">
            <div className="flex items-center gap-2 mb-1">
              <span className="w-9 h-9 rounded-lg bg-accent-50 text-accent-700 flex items-center justify-center flex-shrink-0">
                <i className="ri-bank-card-line"></i>
              </span>
              <div className="min-w-0">
                <h3 className="text-sm font-bold font-heading text-foreground-950">Moyens de paiement activés</h3>
                <p className="text-xs text-foreground-500">Activez un moyen pour le rendre disponible selon la portée choisie ci-dessous.</p>
              </div>
              <span className="ml-auto text-xs px-3 py-1 rounded-full bg-background-100 text-foreground-500 font-medium whitespace-nowrap">
                {enabledProviders.length}/{providers.length} actifs
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
              {providers.map((p) => (
                <div
                  key={p.id}
                  className={`flex items-center gap-3 rounded-lg border p-3 transition-colors ${
                    p.enabled ? 'border-accent-200/70 bg-accent-50/30' : 'border-background-200/70 bg-background-50'
                  }`}
                >
                  <span className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${p.enabled ? 'bg-accent-100 text-accent-700' : 'bg-background-100 text-foreground-500'}`}>
                    <i className="ri-bank-card-line"></i>
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-foreground-900">{p.label}</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${p.enabled ? 'bg-accent-100 text-accent-700' : 'bg-background-100 text-foreground-500'}`}>
                        {p.enabled ? 'Actif' : 'Inactif'}
                      </span>
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-background-100 text-foreground-500">
                        {p.mode === 'live' ? 'Production' : 'Test'}
                      </span>
                    </div>
                    {p.description && <p className="text-[11px] text-foreground-500 mt-0.5 line-clamp-1">{p.description}</p>}
                  </div>
                  <button
                    type="button"
                    onClick={() => void toggleProvider(p)}
                    disabled={savingProviders === p.id}
                    aria-label={p.enabled ? 'Désactiver' : 'Activer'}
                    className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors cursor-pointer flex-shrink-0 disabled:opacity-50 ${
                      p.enabled ? 'bg-accent-500' : 'bg-background-200'
                    }`}
                  >
                    <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${p.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                </div>
              ))}
              {providers.length === 0 && (
                <p className="text-sm text-foreground-500">Aucun fournisseur configuré.</p>
              )}
            </div>
            <p className="text-[11px] text-foreground-400 mt-3 flex items-start gap-1.5">
              <i className="ri-information-line mt-0.5"></i>
              Les clés et identifiants de chaque fournisseur se règlent dans Paramètres → Moyens de paiement.
            </p>
          </section>

          {/* Portée + commission */}
          <section className="bg-background-50 border border-background-200/70 rounded-lg p-5">
            <div className="flex items-center gap-2 mb-4">
              <span className="w-9 h-9 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center flex-shrink-0">
                <i className="ri-settings-4-line"></i>
              </span>
              <div className="min-w-0">
                <h3 className="text-sm font-bold font-heading text-foreground-950">Portée du paiement Zifek</h3>
                <p className="text-xs text-foreground-500">Définissez qui peut payer avec les moyens activés.</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setSettings((s) => ({ ...s, scope: 'subscriptions' }))}
                className={`text-left rounded-lg border p-4 transition-colors cursor-pointer ${
                  settings.scope === 'subscriptions' ? 'border-primary-300 bg-primary-50/50 ring-1 ring-primary-200' : 'border-background-200/70 hover:border-background-300/60'
                }`}
              >
                <div className="flex items-center gap-2">
                  <i className="ri-vip-crown-line text-primary-600"></i>
                  <span className="text-sm font-semibold text-foreground-900">Abonnements Zifek uniquement</span>
                  {settings.scope === 'subscriptions' && <i className="ri-checkbox-circle-fill text-primary-500 ml-auto"></i>}
                </div>
                <p className="text-xs text-foreground-500 mt-1.5">
                  Les moyens activés servent seulement aux commerces pour payer leur abonnement Zifek.
                </p>
              </button>
              <button
                type="button"
                onClick={() => setSettings((s) => ({ ...s, scope: 'all' }))}
                className={`text-left rounded-lg border p-4 transition-colors cursor-pointer ${
                  settings.scope === 'all' ? 'border-primary-300 bg-primary-50/50 ring-1 ring-primary-200' : 'border-background-200/70 hover:border-background-300/60'
                }`}
              >
                <div className="flex items-center gap-2">
                  <i className="ri-store-3-line text-accent-600"></i>
                  <span className="text-sm font-semibold text-foreground-900">Toute la plateforme</span>
                  {settings.scope === 'all' && <i className="ri-checkbox-circle-fill text-primary-500 ml-auto"></i>}
                </div>
                <p className="text-xs text-foreground-500 mt-1.5">
                  En plus des abonnements, les commerces et leurs clients paient sur les boutiques avec ces moyens.
                </p>
              </button>
            </div>

            {/* Commission */}
            <div className="mt-5 pt-4 border-t border-background-200/70">
              <div className="flex flex-col sm:flex-row sm:items-end gap-3">
                <div>
                  <label htmlFor="commission" className="block text-xs font-semibold text-foreground-600 mb-1.5">
                    Commission Zifek par transaction (%)
                  </label>
                  <div className="relative w-40">
                    <input
                      id="commission"
                      type="number"
                      min={0}
                      max={50}
                      step={0.5}
                      value={settings.commission_percent}
                      onChange={(e) => setSettings((s) => ({ ...s, commission_percent: Number(e.target.value) || 0 }))}
                      className="w-full h-10 px-3 pr-9 rounded-lg border border-background-200/70 bg-background-50 text-sm font-semibold text-foreground-900 focus:outline-none focus:border-primary-300"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-foreground-400">%</span>
                  </div>
                  <p className="text-[11px] text-foreground-400 mt-1">6 % par défaut. Modifiable à tout moment.</p>
                </div>
                <button
                  type="button"
                  onClick={() => void saveSettings()}
                  disabled={savingSettings}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 disabled:opacity-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {savingSettings ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-3-line"></i>}
                  Enregistrer
                </button>
              </div>

              {/* Détail des frais */}
              <div className="mt-5 rounded-lg border border-background-200/70 overflow-hidden">
                <div className="px-3 py-2 bg-background-100/60 flex items-center justify-between">
                  <span className="text-[11px] font-semibold text-foreground-600 uppercase tracking-wide">Détail des frais ({settings.commission_percent} %)</span>
                  <span className={`text-[11px] font-semibold ${breakdownTotal === Number(settings.commission_percent) ? 'text-accent-700' : 'text-amber-700'}`}>
                    Total réparti : {breakdownTotal} %
                  </span>
                </div>
                <div className="divide-y divide-background-200/60">
                  {settings.fee_breakdown.map((line, i) => (
                    <div key={`${line.label}-${i}`} className="flex items-center gap-3 px-3 py-2">
                      <span className="text-xs text-foreground-700 flex-1 min-w-0">{line.label}</span>
                      <div className="relative w-24 flex-shrink-0">
                        <input
                          type="number"
                          min={0}
                          max={50}
                          step={0.1}
                          value={line.percent}
                          onChange={(e) => setSettings((s) => ({
                            ...s,
                            fee_breakdown: s.fee_breakdown.map((l, idx) => (idx === i ? { ...l, percent: Number(e.target.value) || 0 } : l)),
                          }))}
                          className="w-full h-8 px-2 pr-6 rounded-md border border-background-200/70 bg-background-50 text-xs font-semibold text-foreground-900 focus:outline-none focus:border-primary-300"
                        />
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-foreground-400">%</span>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="px-3 py-2 bg-background-100/40 text-[11px] text-foreground-500">
                  Tous les frais (FeexPay et Mobile Money) sont inclus dans ces {settings.commission_percent} % : le commerce reçoit le montant net, sans frais supplémentaires.
                </p>
              </div>
            </div>
          </section>

          {/* Transactions */}
          <section className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-4 py-3 border-b border-background-200/70">
              <h3 className="text-sm font-semibold text-foreground-900 flex items-center gap-2">
                <i className="ri-exchange-dollar-line text-accent-500"></i>
                Transactions
              </h3>
              <div className="flex items-center gap-0.5 bg-background-100 rounded-full p-0.5 w-fit">
                <button
                  type="button"
                  onClick={() => setTab('subscriptions')}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${tab === 'subscriptions' ? 'bg-background-50 text-foreground-900 shadow-sm' : 'text-foreground-500'}`}
                >
                  Abonnements ({subscriptions.length})
                </button>
                <button
                  type="button"
                  onClick={() => setTab('merchants')}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${tab === 'merchants' ? 'bg-background-50 text-foreground-900 shadow-sm' : 'text-foreground-500'}`}
                >
                  Commerces ({merchantPayments.length})
                </button>
              </div>
            </div>

            <div className="overflow-x-auto">
              {tab === 'subscriptions' ? (
                subscriptions.length === 0 ? (
                  <p className="py-12 text-center text-sm text-foreground-500">Aucune transaction d&apos;abonnement.</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-foreground-400 border-b border-background-200/70">
                        <th className="py-2 px-4 font-medium">Date</th>
                        <th className="py-2 px-3 font-medium">Commerce</th>
                        <th className="py-2 px-3 font-medium">Formule</th>
                        <th className="py-2 px-3 font-medium">Moyen</th>
                        <th className="py-2 px-3 font-medium">Montant</th>
                        <th className="py-2 px-3 font-medium">Commission</th>
                        <th className="py-2 px-4 font-medium">Statut</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subscriptions.map((s) => {
                        const amount = Number(s.prix) || 0;
                        const commission = Math.round(amount * (Number(settings.commission_percent) || 0)) / 100;
                        return (
                          <tr key={s.id} className="border-b border-background-200/50 last:border-0">
                            <td className="py-2.5 px-4 text-foreground-500 whitespace-nowrap">{formatDate(s.created_at)}</td>
                            <td className="py-2.5 px-3 text-foreground-800 max-w-[180px] truncate">{userNames[Number(s.user_id)] || `#${s.user_id}`}</td>
                            <td className="py-2.5 px-3 whitespace-nowrap">
                              <span className="text-foreground-800">{s.plan || '—'}</span>
                              <span className="text-foreground-400 text-xs ml-1">· {s.period === 'yearly' ? 'annuel' : 'mensuel'}</span>
                            </td>
                            <td className="py-2.5 px-3 text-foreground-600 whitespace-nowrap">{methodLabel(s.payment_method)}</td>
                            <td className="py-2.5 px-3 font-semibold text-foreground-900 whitespace-nowrap">{money(amount, s.monaie || 'MAD')}</td>
                            <td className="py-2.5 px-3 text-primary-700 font-medium whitespace-nowrap">{money(commission, s.monaie || 'MAD')}</td>
                            <td className="py-2.5 px-4">
                              <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${STATUS_STYLE[(s.status || '').toLowerCase()] || 'bg-background-100 text-foreground-600'}`}>
                                {statusLabel(s.status)}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )
              ) : merchantPayments.length === 0 ? (
                <p className="py-12 text-center text-sm text-foreground-500">Aucun paiement commerce enregistré.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-foreground-400 border-b border-background-200/70">
                      <th className="py-2 px-4 font-medium">Date</th>
                      <th className="py-2 px-3 font-medium">Commerce</th>
                      <th className="py-2 px-3 font-medium">Commande</th>
                      <th className="py-2 px-3 font-medium">Moyen</th>
                      <th className="py-2 px-3 font-medium">Montant</th>
                      <th className="py-2 px-3 font-medium">Commission</th>
                      <th className="py-2 px-3 font-medium">Référence</th>
                      <th className="py-2 px-4 font-medium">Statut</th>
                    </tr>
                  </thead>
                  <tbody>
                    {merchantPayments.map((p) => {
                      const amount = Number(p.amount) || 0;
                      const commission = Math.round(amount * (Number(settings.commission_percent) || 0)) / 100;
                      return (
                        <tr key={p.id} className="border-b border-background-200/50 last:border-0">
                          <td className="py-2.5 px-4 text-foreground-500 whitespace-nowrap">{formatDate(p.created_at)}</td>
                          <td className="py-2.5 px-3 text-foreground-800 max-w-[180px] truncate">{userNames[Number(p.user_id)] || `#${p.user_id ?? '—'}`}</td>
                          <td className="py-2.5 px-3 whitespace-nowrap">
                            {orderRefOf(p) === '—' ? (
                              <span className="text-foreground-400">—</span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900 text-xs font-medium">
                                <i className="ri-receipt-line"></i>
                                {orderRefOf(p)}
                              </span>
                            )}
                          </td>
                          <td className="py-2.5 px-3 text-foreground-600 whitespace-nowrap">{methodLabel(p.method)}</td>
                          <td className="py-2.5 px-3 font-semibold text-foreground-900 whitespace-nowrap">{money(amount, p.currency || 'MAD')}</td>
                          <td className="py-2.5 px-3 text-primary-700 font-medium whitespace-nowrap">{money(commission, p.currency || 'MAD')}</td>
                          <td className="py-2.5 px-3 text-foreground-500 max-w-[200px]"><span className="text-xs break-all">{p.reference || '—'}</span></td>
                          <td className="py-2.5 px-4">
                            <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${STATUS_STYLE[(p.status || '').toLowerCase()] || 'bg-background-100 text-foreground-600'}`}>
                              {statusLabel(p.status)}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}